import { useLayoutEffect } from 'react'

import type { LanguageCode } from './url-language-utils'
import { getPathLanguage, withLanguagePath } from './url-language-utils'

const HREFLANG: Record<LanguageCode, string> = {
  en: 'en',
  zh: 'zh-Hans',
  tw: 'zh-Hant',
  ja: 'ja',
  ko: 'ko',
  ar: 'ar',
  es: 'es',
  fr: 'fr',
}

interface ResearchPageSeoHead {
  pagePath: string
  contentLanguage: LanguageCode
  title: string
  description: string
  translatedLanguages?: readonly LanguageCode[]
}

const EMPTY_LANGUAGES: readonly LanguageCode[] = []

export function useResearchPageSeoHead({
  pagePath,
  contentLanguage,
  title,
  description,
  translatedLanguages = EMPTY_LANGUAGES,
}: ResearchPageSeoHead): void {
  useLayoutEffect(() => {
    const pathnameOnMount = window.location.pathname
    const structuredData = document.head.querySelector<HTMLScriptElement>(
      '#research-page-structured-data',
    )
    const previousTitle = document.title
    const previousLanguage = document.documentElement.lang
    const previousDirection = document.documentElement.dir
    const interfaceLanguage = getPathLanguage(window.location.pathname)
      ?? contentLanguage
    const canonicalUrl = `https://mentiss.ai${withLanguagePath(pagePath, contentLanguage)}`

    document.title = title
    document.documentElement.lang = HREFLANG[interfaceLanguage]
    document.documentElement.dir = interfaceLanguage === 'ar' ? 'rtl' : 'ltr'

    const canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
      ?? document.createElement('link')
    canonical.rel = 'canonical'
    canonical.href = canonicalUrl
    document.head.append(canonical)

    const metaDescription = document.head.querySelector<HTMLMetaElement>('meta[name="description"]')
      ?? document.createElement('meta')
    metaDescription.name = 'description'
    metaDescription.content = description
    document.head.append(metaDescription)

    const robots = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]')
      ?? document.createElement('meta')
    robots.name = 'robots'
    robots.content = window.location.hostname === 'mentiss.ai'
      || window.location.hostname === 'www.mentiss.ai'
      ? interfaceLanguage === contentLanguage ? 'index, follow' : 'noindex, follow'
      : 'noindex, nofollow'
    document.head.append(robots)

    document.head.querySelectorAll('link[rel="alternate"][hreflang]')
      .forEach(link => link.remove())
    const alternates: HTMLLinkElement[] = []
    if (translatedLanguages.length > 1) {
      for (const language of translatedLanguages) {
        const link = document.createElement('link')
        link.rel = 'alternate'
        link.hreflang = HREFLANG[language]
        link.href = `https://mentiss.ai${withLanguagePath(pagePath, language)}`
        document.head.append(link)
        alternates.push(link)
      }
      const defaultAlternate = document.createElement('link')
      defaultAlternate.rel = 'alternate'
      defaultAlternate.hreflang = 'x-default'
      defaultAlternate.href = `https://mentiss.ai${withLanguagePath(pagePath, 'en')}`
      document.head.append(defaultAlternate)
      alternates.push(defaultAlternate)
    }

    return () => {
      canonical.remove()
      metaDescription.remove()
      robots.remove()
      alternates.forEach(link => link.remove())
      document.title = previousTitle
      document.documentElement.lang = previousLanguage
      document.documentElement.dir = previousDirection
      if (window.location.pathname !== pathnameOnMount)
        structuredData?.remove()
    }
  }, [contentLanguage, description, pagePath, title, translatedLanguages])
}
